import { Injectable } from '@angular/core';
import { tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
  DprActivity,
  DprActivityCustomData,
  DprConsumableDetail,
  DprDetails,
  DprHseDetail,
  DprPersonnelDetail,
  DprProgressDetail,
  DprProgressWithoutTargetDetail,
  DprTableOfColumnDetail,
  DprWeatherDetail,
  WarningStatus,
} from '@shared/models/dpr';
import { WarningType } from '@shared/enums/dpr';
import { UserDetailsFacade } from '@shared/state/facades';
import { ActivityStreamType, PersonnelVersion } from '@shared/enums';
import {
  compareTime,
  convertTimeToDate,
  hasValue,
  isDefaultFinishTime,
  isDefaultStartTime,
} from '@shared/utils';
import { CustomData } from '@shared/models/project-setup';
import { DataType, UserSession } from '@shared/models';
import cloneDeep from 'lodash.clonedeep';

@UntilDestroy()
@Injectable()
export class DprDetailsWarningService {
  private userDetails: UserSession;

  constructor(private userDetailsFacade: UserDetailsFacade) {
    this.userDetailsFacade.query.userDetails$
      .pipe(
        untilDestroyed(this),
        tap((userDetails) => {
          this.userDetails = userDetails;
        })
      )
      .subscribe();
  }

  updateDprWarningStatus(dprDetails: DprDetails): void {
    const warning = this.recomputeFirstWarning(dprDetails);

    if (warning !== null) {
      dprDetails.firstWarning = warning.firstWarning;
      dprDetails.warningStatusTab = warning.warningStatusTab;
    } else {
      delete dprDetails?.firstWarning;
      delete dprDetails?.warningStatusTab;
    }
  }

  isActivityComplete(dprActivities: DprActivity[]): boolean {
    const activityStreams =
      this.userDetails.selectedSubproject.data.activityStreams || [];

    const primaryActivityStream = activityStreams.find(
      (x) => x.type === ActivityStreamType.Primary
    );

    if (!primaryActivityStream) {
      return true;
    }

    if (
      !this.isTimeIntervalSetComplete(
        dprActivities,
        primaryActivityStream.is24hRequired,
        primaryActivityStream.isNoTimeOverlap
      )
    ) {
      return false;
    }

    return true;
  }

  isParallelActivityComplete(dprParallelActivities: DprActivity[]): boolean {
    const activityStreams =
      this.userDetails.selectedSubproject.data.activityStreams || [];

    const parallelActivityStreams = activityStreams.filter(
      (x) => x.type === ActivityStreamType.Parallel && x.isActive
    );

    if (!parallelActivityStreams?.length) {
      return true;
    }

    return !parallelActivityStreams.some((x) => {
      const parallelActivityItems = (dprParallelActivities || []).filter(
        (y) => x.parallelActivityType === y.parallelActivityType
      );

      return !this.isTimeIntervalSetComplete(
        parallelActivityItems,
        x.is24hRequired,
        x.isNoTimeOverlap
      );
    });
  }

  isCustomDataComplete(dprActivities: DprActivity[]): boolean {
    let result = true;
    const customDataList =
      this.userDetails.selectedSubproject.data.customDataList || [];

    dprActivities.forEach((x) => {
      const mandatoryCustomDataIds = this.getMandatoryCustomDataIds(
        x.activityId,
        x.parallelActivityType
      );

      const dprActivityCustomData = !x.parallelActivityType
        ? x.dprActivityCustomDataList
        : x.dprParallelActivityCustomDataList;

      if (
        mandatoryCustomDataIds.some(
          (y) =>
            !this.isValueSetForMandatoryCustomData(
              dprActivityCustomData,
              y,
              customDataList
            )
        )
      ) {
        result = false;
      }
    });

    return result;
  }

  isWeatherComplete(dprWeatherDetails: DprWeatherDetail[]): boolean {
    let result = true;
    const weatherList =
      this.userDetails.selectedSubproject.data.weatherList || [];

    const activeAndMandatoryWeatherIds =
      this.userDetails.selectedSubproject.data.weatherList
        .filter((x) => x.isMandatory && x.isActive)
        .map((x) => x.id);

    dprWeatherDetails.forEach((x) => {
      const dprWeatherIds = [];

      x.dprWeatherDetailsItems.forEach((y) => {
        const weather = weatherList.find((z) => z.id === y.weatherId);

        switch (weather?.dataTypeId) {
          case DataType.FreeText:
            if (y.value?.trim()) {
              dprWeatherIds.push(y.weatherId);
            }
            break;
          case DataType.List:
          case DataType.Boolean:
            if (
              !String.isNullOrWhiteSpace(y.value?.toString()) &&
              +y.value >= 0
            ) {
              dprWeatherIds.push(y.weatherId);
            }
            break;
          case DataType.Number:
          case DataType.Percentage:
          case DataType.Date:
          case DataType.Datetime:
            if (!String.isNullOrWhiteSpace(y.value?.toString())) {
              dprWeatherIds.push(y.weatherId);
            }
            break;
        }
      });

      if (
        !activeAndMandatoryWeatherIds.every(
          (y) => dprWeatherIds.indexOf(y) >= 0
        )
      ) {
        result = false;
      }
    });

    return result;
  }

  isHseComplete(dprHseDetails: DprHseDetail[]): boolean {
    const hseList = this.userDetails.selectedSubproject.data.hseList || [];

    const activeAndMandatoryHseIds = hseList
      .filter((x) => x.isMandatory && x.isActive)
      .map((x) => x.id);

    const dprHseIds = (dprHseDetails || [])
      .filter((x) => x.today != null && x.today >= 0)
      .map((x) => x.hseId);

    return activeAndMandatoryHseIds.every((x) => dprHseIds.indexOf(x) >= 0);
  }

  isPersonnelComplete(dprPersonnelDetails: DprPersonnelDetail[]): boolean {
    const personnelVersion =
      this.userDetails.selectedSubproject.data.subprojectConfiguration
        .personnelVersion;

    const personnelList =
      this.userDetails.selectedSubproject.data.personnelList || [];

    const activeAndMandatoryPersonnelIds = personnelList
      .filter((x) => x.isMandatory && x.isActive)
      .map((x) => x.id);

    const dprPersonnelIds = (dprPersonnelDetails || [])
      .filter(
        (x) =>
          (personnelVersion === PersonnelVersion.Basic &&
            x.today != null &&
            x.today >= 0) ||
          (personnelVersion === PersonnelVersion.Stock &&
            x.used != null &&
            x.added != null &&
            x.dailyStockDetails &&
            x.dailyStockDetails.totalEndOfDay >= 0) ||
          (personnelVersion === PersonnelVersion.Classic &&
            x.currentDay != null &&
            x.currentDay >= 0)
      )
      .map((x) => x.personnelId);

    return activeAndMandatoryPersonnelIds.every(
      (x) => dprPersonnelIds.indexOf(x) >= 0
    );
  }

  isConsumableComplete(dprConsumableDetails: DprConsumableDetail[]): boolean {
    const isStockEnabled =
      this.userDetails.selectedSubproject.data.subprojectConfiguration
        .enableStockForConsumables;
    const consumableList =
      this.userDetails.selectedSubproject.data.consumableList || [];

    const activeAndMandatoryConsumableIds = consumableList
      .filter((x) => x.isMandatory && x.isActive)
      .map((x) => x.id);

    const dprConsumableIds = (dprConsumableDetails || [])
      .filter(
        (x) =>
          (!isStockEnabled && x.today != null) ||
          (isStockEnabled && x.used != null && x.added != null)
      )
      .map((x) => x.consumableId);

    return activeAndMandatoryConsumableIds.every(
      (x) => dprConsumableIds.indexOf(x) >= 0
    );
  }

  isValueSetForMandatoryCustomData(
    dprActivityCustomDataList: DprActivityCustomData[],
    customDataId: number,
    customDataList: CustomData[]
  ): boolean {
    return (dprActivityCustomDataList || []).some((x) => {
      const customData = (customDataList || []).find(
        (y) => y.id === x.customDataId
      );

      return (
        x.customDataId === customDataId &&
        hasValue(x.value, customData?.dataTypeId)
      );
    });
  }

  isProgressComplete(dprProgressDetails: DprProgressDetail[]): boolean {
    const progressItems =
      this.userDetails.selectedSubproject.data.progress.progressItems;

    if (
      !this.userDetails.selectedSubproject.data.subprojectConfiguration
        .enableProgressTab
    ) {
      return true;
    }

    const activeAndMandatoryProgressIds = progressItems
      .filter((x) => x.isMandatory && x.isActive)
      .map((x) => x.id);

    const dprProgressIds = (dprProgressDetails || [])
      .filter((x) => x.clearedToday != null && x.clearedToday >= 0)
      .map((x) => x.progressId);

    return activeAndMandatoryProgressIds.every(
      (x) => dprProgressIds.indexOf(x) >= 0
    );
  }

  isProgressWithoutTargetComplete(
    dprProgressWithoutTargetDetails: DprProgressWithoutTargetDetail[]
  ): boolean {
    const progressWithoutTargetItems =
      this.userDetails.selectedSubproject.data.progressWithoutTarget
        .progressWithoutTargetItems;

    if (
      !this.userDetails.selectedSubproject.data.subprojectConfiguration
        .enableProgressWithoutTargetTab
    ) {
      return true;
    }

    const activeAndMandatoryProgressWithoutTargetIds =
      progressWithoutTargetItems
        .filter((x) => x.isMandatory && x.isActive)
        .map((x) => x.id);

    const dprProgressWithoutTargetIds = (dprProgressWithoutTargetDetails || [])
      .filter((x) => x.clearedToday != null && x.clearedToday >= 0)
      .map((x) => x.progressWithoutTargetId);

    return activeAndMandatoryProgressWithoutTargetIds.every(
      (x) => dprProgressWithoutTargetIds.indexOf(x) >= 0
    );
  }

  isTableOfColumnsTabComplete(
    dprTableOfColumnDetails: DprTableOfColumnDetail[],
    tableOfColumnId: number
  ): boolean {
    if (!dprTableOfColumnDetails) {
      return true;
    }
    let result = true;
    const tableOfColumn =
      this.userDetails.selectedSubproject.data.tableOfColumns.find(
        (y) => y.id === tableOfColumnId
      );
    const tableOfColumnItemList = tableOfColumn.tableOfColumnItems || [];

    const activeAndMandatoryTableOfColumnItemsIds = tableOfColumnItemList
      .filter((x) => x.isMandatory && x.isActive)
      .map((x) => x.id);

    (dprTableOfColumnDetails || []).forEach((x) => {
      const dprTableOfColumnsIds = [];

      x.dprTableOfColumnDetailItems.forEach((y) => {
        const tableOfColumnItem = tableOfColumnItemList.find(
          (z) => z.id === y.tableOfColumnItemId
        );

        switch (tableOfColumnItem?.dataTypeId) {
          case DataType.FreeText:
            if (y.value?.trim()) {
              dprTableOfColumnsIds.push(y.tableOfColumnItemId);
            }
            break;
          case DataType.List:
          case DataType.Boolean:
            if (
              !String.isNullOrWhiteSpace(y.value?.toString()) &&
              +y.value >= 0
            ) {
              dprTableOfColumnsIds.push(y.tableOfColumnItemId);
            }
            break;
          case DataType.Number:
          case DataType.Percentage:
          case DataType.Date:
          case DataType.Datetime:
            if (!String.isNullOrWhiteSpace(y.value?.toString())) {
              dprTableOfColumnsIds.push(y.tableOfColumnItemId);
            }
            break;
        }
      });

      if (
        !activeAndMandatoryTableOfColumnItemsIds.every(
          (y) => dprTableOfColumnsIds.indexOf(y) >= 0
        )
      ) {
        result = false;
      }
    });

    return result;
  }

  //#region Private methods

  private recomputeFirstWarning(dprDetails: DprDetails): WarningStatus {
    if (
      !this.isActivityComplete(dprDetails.dprActivities) ||
      !this.isParallelActivityComplete(dprDetails.dprParallelActivities)
    ) {
      return {
        firstWarning: WarningType.Activity,
      };
    }

    if (
      !this.isCustomDataComplete(dprDetails.dprActivities) ||
      !this.isCustomDataComplete(dprDetails.dprParallelActivities)
    ) {
      return {
        firstWarning: WarningType.CustomData,
      };
    }

    if (!this.isWeatherComplete(dprDetails.dprWeather?.dprWeatherDetails)) {
      return {
        firstWarning: WarningType.Weather,
      };
    }

    if (!this.isHseComplete(dprDetails.dprHSE?.dprhseDetails)) {
      return {
        firstWarning: WarningType.HSE,
      };
    }

    if (
      !this.isPersonnelComplete(dprDetails.dprPersonnel?.dprPersonnelDetails)
    ) {
      return {
        firstWarning: WarningType.Personnel,
      };
    }

    if (
      !this.isConsumableComplete(dprDetails.dprConsumable?.dprConsumableDetails)
    ) {
      return {
        firstWarning: WarningType.Consumable,
      };
    }

    if (!this.isProgressComplete(dprDetails.dprProgress?.dprProgressDetails)) {
      return {
        firstWarning: WarningType.Progress,
      };
    }

    if (
      !this.isProgressWithoutTargetComplete(
        dprDetails.dprProgressWithoutTarget?.dprProgressWithoutTargetDetails
      )
    ) {
      return {
        firstWarning: WarningType.ProgressWithoutTarget,
      };
    }

    for (const toc of this.userDetails.selectedSubproject.data.tableOfColumns
      .filter((x) => x.isActive)
      .sort((x, y) => x.priority - y.priority)) {
      if (
        !this.isTableOfColumnsTabComplete(
          dprDetails.dprTableOfColumns?.find(
            (x) => x.tableOfColumnId === toc.id
          )?.dprTableOfColumnsDetails,
          toc.id
        )
      ) {
        return {
          firstWarning: WarningType.TableOfColumn,
          warningStatusTab: toc.name,
        };
      }
    }

    return null;
  }

  private isTimeIntervalSetComplete(
    dprActivitiesList: DprActivity[],
    is24hRequired: boolean,
    isNoTimeOverlap: boolean
  ): boolean {
    // create a deep copy to make the object mutated (editable and sortable)
    const dprActivities = cloneDeep(dprActivitiesList) as DprActivity[];

    if (is24hRequired) {
      // one start time from the list must be start of day and
      // one finish time from the list must be end of day
      if (
        !dprActivities?.some((x) =>
          isDefaultStartTime(convertTimeToDate(x.start))
        ) ||
        !dprActivities?.some((x) =>
          isDefaultFinishTime(convertTimeToDate(x.finish))
        )
      ) {
        return false;
      }

      // check that there are no gaps, the entire 24h interval is covered
      if (dprActivities.length > 1) {
        // check if there are no intervals that include the current start time, if the current start is not 00:00
        if (
          dprActivities.some(
            (x) =>
              !isDefaultStartTime(convertTimeToDate(x.start)) &&
              !dprActivities.some(
                (y) =>
                  y.start <= x.start && y.finish >= x.start && x.id !== y.id
              )
          )
        ) {
          return false;
        }

        // check if there are no intervals that include the current finish time, if the current finish is not 23:59
        if (
          dprActivities.some(
            (x) =>
              !isDefaultFinishTime(convertTimeToDate(x.finish)) &&
              !dprActivities.some(
                (y) =>
                  y.start <= x.finish && y.finish >= x.finish && x.id !== y.id
              )
          )
        ) {
          return false;
        }

        // order list by start and finish to be able to get the previous activity
        dprActivities.sort((x, y) => {
          return compareTime(
            convertTimeToDate(x.start),
            convertTimeToDate(y.start)
          )
            ? compareTime(
                convertTimeToDate(x.start),
                convertTimeToDate(y.start)
              )
            : compareTime(
                convertTimeToDate(x.finish),
                convertTimeToDate(y.finish)
              );
        });

        // check if the current start is bigger than the previous finish time
        // if both current and previous times are milestones (start=finish)
        if (
          dprActivities.some((x) => {
            const currentIdx = dprActivities.indexOf(x);
            const previous =
              currentIdx > 0 ? dprActivities[currentIdx - 1] : null;

            return (
              previous &&
              x.start === x.finish &&
              previous.start === previous.finish &&
              x.start > previous.finish
            );
          })
        ) {
          return false;
        }
      }
    }

    if (isNoTimeOverlap && dprActivities.length > 1) {
      if (
        dprActivities.some(
          (x) =>
            !isDefaultStartTime(convertTimeToDate(x.start)) &&
            dprActivities.some(
              (y) => y.finish > x.start && y.start < x.start && x.id !== y.id
            )
        )
      ) {
        return false;
      }

      if (
        dprActivities.some(
          (x) =>
            !isDefaultFinishTime(convertTimeToDate(x.finish)) &&
            dprActivities.some(
              (y) => y.start < x.finish && y.finish > x.finish && x.id !== y.id
            )
        )
      ) {
        return false;
      }

      if (
        dprActivities.some(
          (x) =>
            x.start !== x.finish &&
            dprActivities.some(
              (y) =>
                y.start === x.start && y.finish === x.finish && x.id !== y.id
            )
        )
      ) {
        return false;
      }
    }
    return true;
  }

  private getMandatoryCustomDataIds(
    activityId: number,
    parallelActivityType: number
  ): number[] {
    const activityMobilisations =
      this.userDetails.selectedSubproject.data.activityMobilisationList.filter(
        (x) =>
          x.activityId === activityId &&
          (x.parallelActivityType ?? 0) === (parallelActivityType ?? 0) &&
          x.isActive
      );

    return activityMobilisations
      .filter((x) => x.isMandatory)
      .map((x) => x.customDataId);
  }

  //#endregion
}
